import { type FC, type ReactNode, useCallback, type MouseEventHandler } from 'react';
import { graphql, useFragment } from 'react-relay';
import { FormattedMessage, defineMessages, useIntl, type MessageValue } from 'dibs-react-intl';
import classnames from 'classnames';
import { Link } from 'dibs-elements/exports/Link.tsx';
import getBuyerSupportLink from 'dibs-support-link/exports/getBuyerSupportLink.ts';
import { trackEvent } from 'dibs-tracking';
import BuyerIncentiveCopy from 'dibs-buyer-incentives/exports/BuyerIncentiveCopy.ts';
import { getBuyerProtectionDuration } from 'dibs-buyer-incentives/exports/getBuyerProtectionDuration.ts';
import { useServerVarsContext } from '../../../global/ServerVarsContext/ServerVarsContext.tsx';

import CheckShield from 'dibs-icons/exports/CheckShield.ts';
import MoneyBackGuarantee from 'dibs-icons/exports/MoneyBackGuarantee.ts';
import Cancellation24Hours from 'dibs-icons/exports/Cancellation24Hours.ts';
import Storefront from 'dibs-icons/exports/Storefront.ts';
import PriceParity from 'dibs-icons/exports/PriceParity.ts';
import Delivery from 'dibs-icons/exports/Delivery.ts';

import { PdpSharedBuyerIncentivesLink } from '../PdpSharedBuyerIncentivesLink/PdpSharedBuyerIncentivesLink.tsx';

import { type PdpSharedBuyerIncentives_user$key } from './__generated__/PdpSharedBuyerIncentives_user.graphql.ts';

import dibsCss from 'dibs-css';
import styles from './PdpSharedBuyerIncentives.scss';

const Incentive: FC<{
    title: ReactNode;
    description: ReactNode;
    icon?: ReactNode;
}> = ({ title, description, icon }) => {
    const { isMobile } = useServerVarsContext();
    return (
        <div className={classnames(dibsCss.sassyFontBodyMedium)}>
            {icon && (
                <span
                    className={classnames(
                        dibsCss.flex,
                        dibsCss.itemsCenter,
                        dibsCss.fillShagreen800,
                        dibsCss.mbSmaller,
                        {
                            [dibsCss.justifyCenter]: !isMobile,
                            [dibsCss.bgShagreen100]: !isMobile,
                            [styles.circle]: !isMobile,
                        }
                    )}
                >
                    {icon}
                </span>
            )}

            <div className={classnames(dibsCss.mbXxsmall, dibsCss.sassyFontBodyMediumHeavy)}>
                {title}
            </div>
            {description}
        </div>
    );
};

const getBuyerProtectionTier = (code?: string | null): ReactNode => {
    const tradeMemberCopy = (
        <FormattedMessage id="PdpSharedBuyerIncentives.member" defaultMessage="rewards member" />
    );

    switch (code) {
        case 'MEMBER':
            return tradeMemberCopy;
        case 'BRONZE':
            return (
                <FormattedMessage
                    id="PdpSharedBuyerIncentives.bronze"
                    defaultMessage="Bronze rewards member"
                />
            );
        case 'SILVER':
            return (
                <FormattedMessage
                    id="PdpSharedBuyerIncentives.silver"
                    defaultMessage="Silver rewards member"
                />
            );
        case 'GOLD':
            return (
                <FormattedMessage
                    id="PdpSharedBuyerIncentives.gold"
                    defaultMessage="Gold rewards member"
                />
            );
        case 'PLATINUM':
            return (
                <FormattedMessage
                    id="PdpSharedBuyerIncentives.platinum"
                    defaultMessage="Platinum rewards member"
                />
            );
        default:
            return tradeMemberCopy;
    }
};

const copy = defineMessages({
    buyerProtectionReadMoreLinkA11y: {
        id: 'PdpSharedBuyerIncentives.readMoreLinkA11y',
        defaultMessage: 'Learn more about buyer protection',
    },
});

const userFragment = graphql`
    fragment PdpSharedBuyerIncentives_user on User {
        tradeFirm @include(if: $isTrade) {
            rewards {
                currentProgram {
                    programInfo {
                        code
                    }
                    childPrograms(filter: "ENHANCED_BUYER_PROTECTION") {
                        code
                    }
                }
            }
        }
    }
`;

export const PdpSharedBuyerIncentives: FC<{
    user: PdpSharedBuyerIncentives_user$key | null | undefined;
    showLearnMoreLink?: boolean;
    columnLayoutOverride?: boolean;
}> = ({ user: userRef, showLearnMoreLink = true, columnLayoutOverride = false }) => {
    const intl = useIntl();
    const user = useFragment(userFragment, userRef);
    const { isMobile } = useServerVarsContext();

    const moreInfoClick: MouseEventHandler = useCallback(event => {
        trackEvent(
            {
                category: 'product',
                action: 'More Information click',
                label: 'pdp description | Authenticity Guarantee',
            },
            event
        );
    }, []);

    const buyerProtectionLearnMoreClick: MouseEventHandler = useCallback(event => {
        trackEvent(
            {
                category: 'product',
                action: 'More Information click',
                label: 'pdp description | Buyer Protection',
            },
            event
        );
    }, []);

    const buyerProtectionLearnMoreHandler: MessageValue = useCallback(
        (chunks: ReactNode) => (
            <Link
                href={getBuyerSupportLink('/en-us/articles/14552578793371')}
                onClick={buyerProtectionLearnMoreClick}
                ariaLabel={intl.formatMessage(copy.buyerProtectionReadMoreLinkA11y)}
                target="_blank"
                dataTn="buyer-protection-link"
            >
                {chunks}
            </Link>
        ),
        [intl, buyerProtectionLearnMoreClick]
    );

    const tradeUserRewardsProgram = user?.tradeFirm?.rewards?.currentProgram;
    const tradeProtectionTierCode = tradeUserRewardsProgram?.programInfo?.code;
    const tradeProtectionDurationCode = tradeUserRewardsProgram?.childPrograms?.[0]?.code;
    const showTradeRewardsCopy = !!tradeProtectionDurationCode;

    return (
        <>
            <div
                className={classnames(
                    styles.wrapper,
                    styles.isDibsPromiseVariant,
                    dibsCss.grid,
                    dibsCss.gapXLarge,
                    dibsCss.gapYMedium,
                    {
                        [styles.columnOverride]: columnLayoutOverride || isMobile,
                    }
                )}
            >
                <Incentive
                    icon={<CheckShield variation="24" />}
                    title={
                        <FormattedMessage
                            id="PdpSharedBuyerIncentives.authenticityGuarantee"
                            defaultMessage="Authenticity Guarantee"
                        />
                    }
                    description={
                        <FormattedMessage
                            id="PdpSharedBuyerIncentives.authenticityGuaranteeDescription"
                            defaultMessage="In the unlikely event there’s an issue with an item’s authenticity, contact us within 1 year for a full refund. <link>Details</link>"
                            values={{
                                link: text => (
                                    <Link
                                        href={getBuyerSupportLink(
                                            '/en-us/articles/20416652048027-Authenticity-Guarantee-Policy'
                                        )}
                                        onClick={moreInfoClick}
                                        target="_blank"
                                    >
                                        {text}
                                    </Link>
                                ),
                            }}
                        />
                    }
                />

                <Incentive
                    title={<BuyerIncentiveCopy name="moneyBackGuarantee" />}
                    icon={<MoneyBackGuarantee variation="24" />}
                    description={
                        showTradeRewardsCopy ? (
                            <FormattedMessage
                                id="PdpSharedBuyerIncentives.buyerProtectionTradeDesc"
                                defaultMessage="If your item is not as described, is damaged in transit, or does not arrive, contact us for a full refund. As a {tier}, you get {days} days to report any issues you have with the order. <link>Details</link>"
                                values={{
                                    link: buyerProtectionLearnMoreHandler,
                                    tier: getBuyerProtectionTier(tradeProtectionTierCode),
                                    days: getBuyerProtectionDuration(tradeProtectionDurationCode),
                                }}
                            />
                        ) : (
                            <FormattedMessage
                                id="PdpSharedBuyerIncentives.buyerProtectionDesc"
                                defaultMessage="If your item is not as described, is damaged in transit, or does not arrive, contact us within 7 days for a full refund. <link>Details</link>"
                                values={{ link: buyerProtectionLearnMoreHandler }}
                            />
                        )
                    }
                />

                <Incentive
                    icon={<Cancellation24Hours variation="24" />}
                    title={
                        <FormattedMessage
                            id="PdpSharedBuyerIncentives.freeCancellation"
                            defaultMessage="24-Hour Cancellation"
                        />
                    }
                    description={
                        <FormattedMessage
                            id="PdpSharedBuyerIncentives.freeCancellationDescription"
                            defaultMessage="You have a 24-hour grace period in which to reconsider your purchase, with no questions asked."
                        />
                    }
                />

                <Incentive
                    icon={<Storefront variation="24" />}
                    title={
                        <FormattedMessage
                            id="PdpSharedBuyerIncentives.vettedSellers"
                            defaultMessage="Vetted Professional Sellers"
                        />
                    }
                    description={
                        <FormattedMessage
                            id="PdpSharedBuyerIncentives.vettedSellersDescription"
                            defaultMessage="Our world-class sellers must adhere to strict standards for service and quality, maintaining the integrity of our listings."
                        />
                    }
                />

                <Incentive
                    icon={<PriceParity variation="24" />}
                    title={
                        <FormattedMessage
                            id="PdpSharedBuyerIncentives.priceMatch"
                            defaultMessage="Price-Match Guarantee"
                        />
                    }
                    description={
                        <FormattedMessage
                            id="PdpSharedBuyerIncentives.priceMatchDescription"
                            defaultMessage="If you find that a seller listed the same item for a lower price elsewhere, we’ll match it."
                        />
                    }
                />

                <Incentive
                    icon={<Delivery variation="24" />}
                    title={
                        <FormattedMessage
                            id="PdpSharedBuyerIncentives.globalDelivery"
                            defaultMessage="Trusted Global Delivery"
                        />
                    }
                    description={
                        <FormattedMessage
                            id="PdpSharedBuyerIncentives.globalDeliveryDescription"
                            defaultMessage="Our best-in-class carrier network provides specialized shipping options worldwide, including custom delivery."
                        />
                    }
                />
            </div>
            {showLearnMoreLink && <PdpSharedBuyerIncentivesLink />}
        </>
    );
};
